/**
 * Shared navigation-state hooks - use these on any NEW list/dashboard screen
 * so it restores filters/sort/tab/scroll instead of resetting when the user
 * returns via the Back button or Esc key.
 *
 * useScreenState: drop-in replacement for useState whose value mirrors into
 * the shared NavigationStateContext (see contexts/NavigationStateContext.js),
 * so it survives the component unmounting when the user navigates away
 * (Back button, Esc) and returns - reset only on a full page reload (in-memory
 * store, scoped to the current browser tab session).
 *
 * useScrollRestoration: saves/restores the scroll position of the app's
 * single scroll container (`<main>` in Layout.js) per screen key.
 *
 * How to wire up a new screen:
 *   const [filters, setFilters] = useScreenState('myScreen.filters', { status: '' });
 *   const [activeTab, setActiveTab] = useScreenState('myScreen.activeTab', 'overview');
 *   useScrollRestoration('myScreen', !loading); // call once `loading` is declared
 *
 * Notes:
 * - `key` must be globally unique across the app, e.g. `<screenName>.<fieldName>`.
 * - Only use for ephemeral list/table UI state (filters, sort, tabs, scroll,
 *   pagination). Do NOT use for in-progress forms - those are out of scope
 *   by design and should keep plain useState.
 * - If a derived effect resets a persisted value on a dependency change (e.g.
 *   "clear search when category changes"), guard it with a PREVIOUS-VALUE ref
 *   comparison, not a "did mount" boolean ref - React.StrictMode double-invokes
 *   mount effects, which flips a boolean guard before the real first run and
 *   wipes a just-restored value. See MasterData.js's `prevCategoryRef` for the
 *   correct pattern.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigationStateStore } from '../contexts/NavigationStateContext';

export function useScreenState(key, initialValue) {
  const { getScreenState, setScreenState } = useNavigationStateStore();
  const [value, setValue] = useState(() => getScreenState(key, initialValue));

  const setAndPersist = useCallback((next) => {
    setValue((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      setScreenState(key, resolved);
      return resolved;
    });
  }, [key, setScreenState]);

  return [value, setAndPersist];
}

export function useScrollRestoration(key, ready = true) {
  const { getScreenState, setScreenState } = useNavigationStateStore();
  const restoredRef = useRef(false);

  // Restore once data is ready to scroll into
  useEffect(() => {
    if (!ready || restoredRef.current) return;
    const main = document.querySelector('main');
    const savedY = getScreenState(key, 0);
    if (main && savedY) {
      main.scrollTop = savedY;
    }
    restoredRef.current = true;
  }, [ready, key, getScreenState]);

  // Track scroll position while mounted
  useEffect(() => {
    const main = document.querySelector('main');
    if (!main) return;
    const handleScroll = () => setScreenState(key, main.scrollTop);
    main.addEventListener('scroll', handleScroll, { passive: true });
    return () => main.removeEventListener('scroll', handleScroll);
  }, [key, setScreenState]);
}
